import callAPI from '../../../../API/callSecureAPI'
import endpoints from '../../../../API/endpoints'
import store from '../../../../redux/store'
import { PubMedArticle, PubMedSearchParams, PubMedSearchArticleTypes } from './types'

export type SelectedArticlesMap = { [pmid: string]: boolean }

/**
 * Run PubMed esearch only. Returns all matching PMIDs (up to the requested retmax, max 1000) and the total result count.
 * Use this to kick off a paginated search — store the IDs in state and fetch articles page-by-page.
 */
export const searchPubMedIds = async (
    queryString: string,
    token: string,
    retmax: number = 500
): Promise<{ ids: string[]; total: number }> => {
    const url = `${endpoints.searchPubMedIds.url}?${new URLSearchParams({
        query: queryString,
        retmax: String(retmax),
    }).toString()}`
    return callAPI(url, endpoints.searchPubMedIds.method, token)
}

/**
 * Fetch full article data for a specific list of PMIDs.
 */
export const fetchPubMedArticles = async (ids: string[], token: string): Promise<any[]> => {
    const res = await callAPI(endpoints.fetchPubMedArticles.url, endpoints.fetchPubMedArticles.method, token, { ids })
    return res?.data ?? []
}

export type DuplicateInfo = {
    isDuplicate: boolean
    matchedOn: 'pmid' | 'title'
    collection: 'draft' | 'evidence'
}

/**
 * Check a list of articles for duplicates in the Draft and Evidence collections.
 * Checks by pubmedId first, then by title for any not matched by PMID.
 * Returns a map of pmid → DuplicateInfo for any duplicates found.
 */
export const checkForDuplicates = async (
    articles: Array<{ pmid: string; title: string }>,
    token: string
): Promise<Record<string, DuplicateInfo>> => {
    const state = store.getState()
    const strategyId = state.strategy._id
    return callAPI(endpoints.checkDuplicates.url, endpoints.checkDuplicates.method, token, { articles, strategyId })
}

/**
 * Send the given PubMed articles to the backend for import.
 * Returns the list of imported drafts, each paired with the original article.
 */
export const importSelectedPubMedArticles = async (
    selected: PubMedArticle[],
    token: string
): Promise<Array<{ draftId: string; article: PubMedArticle }>> => {
    if (selected.length === 0) {
        return []
    }

    const state = store.getState()
    const strategyId = state.strategy._id
    const organizationId = state.strategy.organizationId

    const response = await callAPI(
        endpoints.importExternalEvidence.url,
        endpoints.importExternalEvidence.method,
        token,
        {
            selectedArticles: selected,
            context: { strategyId, organizationId },
        }
    )

    const added: Array<{ pmid: string; title: string; draftId: string }> = response?.data?.added || []
    const articleByPmid = Object.fromEntries(selected.map((a) => [a.pmid, a]))

    return added
        .filter((item) => item.draftId && articleByPmid[item.pmid])
        .map((item) => ({ draftId: item.draftId, article: articleByPmid[item.pmid] }))
}

function hasPubMedFieldTag(term: string): boolean {
    return /\[[^\]]+\]$/.test(term.trim())
}

function formatPubMedTerm(term: string): string {
    const trimmed = term.trim()

    if (!trimmed) return ''

    // Already quoted, already field-tagged, or manually using boolean logic.
    if (trimmed.startsWith('"') || hasPubMedFieldTag(trimmed) || /\b(AND|OR|NOT)\b/i.test(trimmed)) {
        return trimmed
    }

    // Quote multi-word terms and common punctuation phrases.
    if (/\s|[-/]/.test(trimmed)) {
        return `"${trimmed}"`
    }

    return trimmed
}

/**
 * Build a PubMed OR group from a comma-separated string.
 * E.g. "d1, d2" with tag "[tiab]" => "(d1[tiab] OR d2[tiab])"
 * Ported from server/services/external-evidence-service.js
 */
function buildOrGroup(raw: string | undefined, tagSuffix: string = ''): string | null {
    if (typeof raw !== 'string') return null
    const parts = raw
        .split(/[;,]/) // Updated to handle the ";" separator per Kate's comment in weekly meeting ... (I hope).
        .map((s) => s.trim())
        .filter((s) => s.length > 0)

    if (!parts.length) return null

    const taggedParts = parts.map((p) => {
        const formatted = formatPubMedTerm(p)

        // Do not append [tiab], [ad], [Author], etc. if user already supplied a PubMed field tag.
        if (hasPubMedFieldTag(formatted)) {
            return formatted
        }

        return `${formatted}${tagSuffix}`
    })

    if (taggedParts.length === 1) {
        return taggedParts[0]
    }
    return `(${taggedParts.join(' OR ')})`

    //If we put in the multi-select groups for outcomes then I'd want those separate groups to be separated by ORs
}

/**
 * Build the full PubMed query string from search params.
 * Ported from server/services/external-evidence-service.js
 */
export function buildPubMedQuery(params: PubMedSearchParams): string {
    const filterMap: { [key: string]: string } = {
        booksanddocuments: 'booksdocs',
        clinicaltrial: 'clinicaltrial',
        metaanalysis: 'meta-analysis',
        randomizedcontrolledtrial: 'randomizedcontrolledtrial',
        review: 'review',
        systematicreview: 'systematicreview',
    }

    const termParts: string[] = []

    const diseaseTerm = buildOrGroup(params.diseaseArea, '[tiab]')
    if (diseaseTerm) termParts.push(diseaseTerm)

    const interventionsTerm = buildOrGroup(params.interventions, '[tiab]')
    if (interventionsTerm) termParts.push(interventionsTerm)

    const sponsorTerm = buildOrGroup(params.sponsor, '[ad]')
    if (sponsorTerm) termParts.push(sponsorTerm)

    const outcomesTerm = buildOrGroup(params.outcomes, '')
    if (outcomesTerm) termParts.push(outcomesTerm)

    const authorsTerm = buildOrGroup(params.authors, '[Author]')
    if (authorsTerm) termParts.push(authorsTerm)

    // Article type filters from the boolean object
    const articleTypes = params.articleTypes || ({} as PubMedSearchArticleTypes)
    const filterParts = Object.keys(articleTypes)
        .filter((key) => articleTypes[key as keyof PubMedSearchArticleTypes])
        .map((key) => key.toLowerCase().replace(/\s+/g, ''))
        .map((normalized) => filterMap[normalized])
        .filter(Boolean)
        .map((f) => `${f}[Filter]`)

    let term = termParts.join(' AND ')
    if (filterParts.length > 0) {
        term += (term ? ' AND ' : '') + '(' + filterParts.join(' OR ') + ')'
    }

    // Date constraints
    const startDate = params.startDate ? params.startDate.format('YYYY-MM') : null
    const endDate = params.endDate ? params.endDate.format('YYYY-MM') : null
    let query = term || ''
    if (startDate && endDate) {
        query += ` AND ("${startDate}"[Date - Publication] : "${endDate}"[Date - Publication])`
    } else if (startDate) {
        query += ` AND ("${startDate}"[Date - Publication] : "3000"[Date - Publication])`
    } else if (endDate) {
        query += ` AND ("1855"[Date - Publication] : "${endDate}"[Date - Publication])`
    }

    if (params.openAccessOnly) {
        query += (query ? ' AND ' : '') + '"pubmed pmc"[sb]'
    }

    return query
}
